import { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { apiFetch, readJsonResponse } from '../api/client';
import { withScheduleGaps } from './chartScheduleGaps';

const PERIOD_OPTIONS = [
  { value: 'all', label: 'Вся жизнь' },
  { value: '1y', label: '1 год' },
  { value: '90d', label: '90 дней' },
  { value: '30d', label: '30 дней' },
];

function formatDateTime(value) {
  if (!value) {
    return '—';
  }
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function formatAxisDate(value, period) {
  if (!value) {
    return '';
  }
  const options =
    period === '30d'
      ? { day: '2-digit', month: '2-digit' }
      : { month: 'short', year: '2-digit' };
  return new Intl.DateTimeFormat('ru-RU', options).format(new Date(value));
}

export function formatVolts(millivolts) {
  const value = Number(millivolts);
  if (!Number.isFinite(value)) {
    return '—';
  }
  return `${(value / 1000).toFixed(3)} В`;
}

function VoltageTooltip({ active, payload }) {
  if (!active || !payload?.[0]?.payload) {
    return null;
  }

  const point = payload[0].payload;
  return (
    <div className="max-w-[15rem] rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-xl dark:border-slate-700 dark:bg-slate-950">
      <p className="font-semibold text-slate-900 dark:text-white">
        Минимум: {formatVolts(point.minimum_voltage_mv)}
      </p>
      <p className="mt-1 text-slate-500">Зафиксирован: {formatDateTime(point.minimum_at)}</p>
      <p className="text-slate-500">Минимум повторился: {point.minimum_count || 0}</p>
      <p className="text-slate-500">Замеров в интервале: {point.sample_count}</p>
    </div>
  );
}

function VoltageDot(props) {
  const { cx, cy, payload } = props;
  if (!Number.isFinite(cx) || !Number.isFinite(cy)) {
    return null;
  }
  const color = voltageColor(payload.minimum_voltage_mv);
  return <circle cx={cx} cy={cy} r={3} fill={color} stroke="white" strokeWidth={1} />;
}

/** A hole only when the next sample is later than this device's link interval. */
export function buildVoltageChartData(points, _bucketSeconds, periodSeconds) {
  return withScheduleGaps(points, periodSeconds, { minimum_voltage_mv: null });
}

/** Paint each minimum until the next sample. A real hole stays uncolored. */
export function buildVoltageBands(chartData, bucketSeconds) {
  const bucketMs = Math.max(1, Number(bucketSeconds) || 1) * 1000;
  const bands = [];

  for (let index = 0; index < (chartData || []).length; index += 1) {
    const point = chartData[index];
    if (point.is_gap || point.minimum_voltage_mv == null || !Number.isFinite(point.bucket_ms)) {
      continue;
    }

    const following = chartData[index + 1];
    const reachesNext =
      following && !following.is_gap && Number.isFinite(following.bucket_ms) && following.bucket_ms > point.bucket_ms;
    bands.push({
      ...point,
      x1: point.bucket_ms,
      x2: reachesNext ? following.bucket_ms : point.bucket_ms + bucketMs,
    });
  }

  return bands;
}

/** Continuous rainbow: lower voltage is redder, a fresh cell is greener. */
export function voltageColor(millivolts) {
  const value = Number(millivolts);
  if (!Number.isFinite(value)) {
    return 'hsl(0 0% 60%)';
  }
  const normalized = Math.min(1, Math.max(0, (value - 2500) / 1100));
  return `hsl(${Math.round(normalized * 120)} 75% 42%)`;
}

export function buildVoltageGradient(bands) {
  const validBands = (bands || [])
    .filter((band) => Number.isFinite(band.x1) && Number.isFinite(band.x2) && band.x2 > band.x1)
    .sort((left, right) => left.x1 - right.x1);
  if (!validBands.length) {
    return null;
  }

  const x1 = validBands[0].x1;
  const x2 = validBands.reduce((maximum, band) => Math.max(maximum, band.x2), x1);
  const span = Math.max(1, x2 - x1);
  const stops = [];
  let previousEnd = x1;

  validBands.forEach((band, index) => {
    const start = Math.min(1, Math.max(0, (band.x1 - x1) / span));
    const end = Math.min(1, Math.max(0, (band.x2 - x1) / span));
    const color = voltageColor(band.minimum_voltage_mv);
    if (band.x1 > previousEnd) {
      const holeStart = Math.min(1, Math.max(0, (previousEnd - x1) / span));
      const holeEnd = Math.min(1, Math.max(0, (band.x1 - x1) / span));
      stops.push({ key: `${index}-hole-start`, offset: holeStart, color: 'transparent' });
      stops.push({ key: `${index}-hole-end`, offset: holeEnd, color: 'transparent' });
    }

    stops.push({ key: `${index}-start`, offset: start, color });
    stops.push({ key: `${index}-end`, offset: end, color });
    previousEnd = Math.max(previousEnd, band.x2);
  });

  return { x1, x2, stops };
}

function csvCell(value) {
  if (value == null) {
    return '';
  }
  const text = String(value);
  if (/[",\r\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

function moscowDay(date) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Moscow',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

/** WFKA00000881_2026-10-09.csv — serial plus the Moscow export date. A selection adds its days. */
export function voltageExportFilename(serial, date = new Date(), range) {
  const safe = String(serial || 'device').trim().replace(/[^\w.-]+/g, '_') || 'device';
  const day = moscowDay(date);
  if (range?.from && range?.to) {
    const end = new Date(new Date(range.to).getTime() - 1);
    return `${safe}_${day}_${moscowDay(new Date(range.from))}_${moscowDay(end)}.csv`;
  }
  return `${safe}_${day}.csv`;
}

/** X-axis milliseconds under the pointer. Recharts passes them as activeLabel. */
export function chartActiveMs(state) {
  const value = Number(state?.activeLabel);
  return Number.isFinite(value) ? value : null;
}

/**
 * Inclusive chart drag becomes a half-open [from, to) for raw events.
 * The last selected bucket is included by extending `to` one bucket past its start.
 */
export function selectionExportRange(startMs, endMs, bucketSeconds) {
  const start = Number(startMs);
  const end = Number(endMs);
  if (!Number.isFinite(start) || !Number.isFinite(end)) {
    return null;
  }
  const fromMs = Math.min(start, end);
  const toMs = Math.max(start, end);
  const bucketMs = Math.max(1, Number(bucketSeconds) || 86400) * 1000;
  return {
    from: new Date(fromMs).toISOString(),
    to: new Date(toMs + bucketMs).toISOString(),
  };
}

/** One row per stored event. voltage is the raw millivolt column, not a bucket minimum. */
export function buildVoltageRawCsv(points) {
  const lines = ['id,inserted,voltage,device_version'];
  for (const point of points || []) {
    lines.push(
      [csvCell(point.id), csvCell(point.inserted), csvCell(point.voltage), csvCell(point.device_version)].join(','),
    );
  }
  return `${lines.join('\n')}\n`;
}

/** Lifetime battery minima. Every bucket retains its exact minimum and occurrence count. */
export default function DeviceVoltageHistory({ deviceId, onLoadingChange }) {
  const gradientId = `voltage-gradient-${useId().replaceAll(':', '')}`;
  const [period, setPeriod] = useState('30d');
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [selection, setSelection] = useState(null);
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef(null);
  const bucketSecondsRef = useRef(null);
  const downloadRef = useRef(null);
  const onLoadingChangeRef = useRef(onLoadingChange);
  onLoadingChangeRef.current = onLoadingChange;

  useEffect(() => {
    if (!deviceId) {
      setIsLoading(false);
      onLoadingChangeRef.current?.(false);
      return undefined;
    }

    const abortController = new AbortController();
    async function load() {
      setIsLoading(true);
      onLoadingChangeRef.current?.(true);
      setError(null);
      try {
        const response = await apiFetch(
          `/api/devices/${deviceId}/voltage-history?period=${encodeURIComponent(period)}`,
          { signal: abortController.signal },
        );
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body.message || `Ошибка ${response.status}`);
        }
        const payload = await readJsonResponse(response, 'История напряжения');
        if (!abortController.signal.aborted) {
          setData(payload);
        }
      } catch (loadError) {
        if (abortController.signal.aborted || loadError?.name === 'AbortError') {
          return;
        }
        setData(null);
        setError(loadError.message || 'Не удалось загрузить напряжение');
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
          onLoadingChangeRef.current?.(false);
        }
      }
    }

    load();
    return () => abortController.abort();
  }, [deviceId, period]);

  async function downloadRawVoltage(range) {
    if (!deviceId || isExporting) {
      return;
    }

    setIsExporting(true);
    setExportError(null);
    try {
      const query = new URLSearchParams();
      if (range?.from) {
        query.set('from', range.from);
      }
      if (range?.to) {
        query.set('to', range.to);
      }
      const suffix = query.toString() ? `?${query}` : '';
      const response = await apiFetch(`/api/devices/${deviceId}/voltage-raw${suffix}`);
      const payload = await readJsonResponse(response, 'Сырые данные напряжения');
      if (!response.ok) {
        throw new Error(payload.message || `Ошибка ${response.status}`);
      }

      const blob = new Blob([buildVoltageRawCsv(payload.points)], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = voltageExportFilename(payload.serial_number, new Date(), range);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (exportFailure) {
      setExportError(exportFailure.message || 'Не удалось скачать сырые данные');
    } finally {
      setIsExporting(false);
    }
  }

  bucketSecondsRef.current = data?.bucket_seconds;
  downloadRef.current = downloadRawVoltage;

  function beginSelection(state) {
    const ms = chartActiveMs(state);
    if (ms == null || isExporting) {
      return;
    }
    dragRef.current = { start: ms, end: ms };
    setSelection(dragRef.current);
    setDragging(true);
  }

  function moveSelection(state) {
    if (!dragRef.current) {
      return;
    }
    const ms = chartActiveMs(state);
    if (ms == null) {
      return;
    }
    dragRef.current = { start: dragRef.current.start, end: ms };
    setSelection(dragRef.current);
  }

  function finishSelection() {
    const drag = dragRef.current;
    if (!drag) {
      return;
    }
    dragRef.current = null;
    setDragging(false);
    setSelection(null);
    if (drag.start === drag.end) {
      return;
    }
    const range = selectionExportRange(drag.start, drag.end, bucketSecondsRef.current);
    if (range) {
      void downloadRef.current(range);
    }
  }

  useEffect(() => {
    if (!dragging) {
      return undefined;
    }
    function onUp() {
      finishSelection();
    }
    window.addEventListener('mouseup', onUp);
    return () => window.removeEventListener('mouseup', onUp);
  }, [dragging]);

  const points = data?.points || [];
  const chartData = useMemo(
    () => buildVoltageChartData(data?.points || [], data?.bucket_seconds, data?.connection_period_seconds),
    [data?.bucket_seconds, data?.connection_period_seconds, data?.points],
  );
  const voltageBands = useMemo(
    () => buildVoltageBands(chartData, data?.bucket_seconds),
    [chartData, data?.bucket_seconds],
  );
  const voltageGradient = useMemo(() => buildVoltageGradient(voltageBands), [voltageBands]);

  return (
    <section className="mt-4 rounded-xl border border-slate-200 bg-slate-50/60 p-3 dark:border-slate-800 dark:bg-slate-900/30">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-slate-500">Напряжение</p>
          <h2 className="mt-0.5 text-sm font-semibold text-slate-900 dark:text-white">
            Минимумы напряжения за жизнь устройства
          </h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Каждый цветной интервал хранит точный минимум: краснее — ниже, зеленее — выше
          </p>
          <button
            type="button"
            onClick={() => void downloadRawVoltage()}
            disabled={!deviceId || isExporting}
            className="mt-2 rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-900"
          >
            {isExporting ? 'Скачиваем…' : 'Скачать сырые данные'}
          </button>
          {exportError ? <p className="mt-1 text-[11px] text-rose-600 dark:text-rose-300">{exportError}</p> : null}
        </div>
        <div className="flex flex-wrap gap-1 rounded-lg bg-slate-100 p-1 dark:bg-slate-800/80">
          {PERIOD_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setPeriod(option.value)}
              className={`rounded-md px-2 py-1 text-[11px] font-medium transition ${
                period === option.value
                  ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-950 dark:text-white'
                  : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading && !data ? (
        <div className="mt-3 h-56 animate-pulse rounded-lg bg-slate-200/70 dark:bg-slate-800" />
      ) : error ? (
        <p className="mt-3 rounded-lg border border-amber-300/40 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">
          {error}
        </p>
      ) : points.length === 0 ? (
        <p className="mt-3 rounded-lg border border-slate-200 bg-white px-3 py-8 text-center text-xs text-slate-500 dark:border-slate-700 dark:bg-slate-950/40">
          Устройство ещё не передавало напряжение
        </p>
      ) : (
        <>
          <div className="mt-3 grid gap-2 sm:grid-cols-3">
            <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-950/50">
              <p className="text-[10px] uppercase tracking-wide text-slate-500">Худший минимум</p>
              <p className="text-lg font-semibold tabular-nums text-slate-900 dark:text-white">
                {formatVolts(data.worst_voltage_mv)}
              </p>
              <p className="text-[11px] text-slate-500">
                {data.worst_voltage_count} раз · {formatDateTime(data.worst_at)}
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-950/50">
              <p className="text-[10px] uppercase tracking-wide text-slate-500">Интервал графика</p>
              <p className="text-lg font-semibold tabular-nums text-slate-900 dark:text-white">
                {data.bucket_label || '—'}
              </p>
              <p className="text-[11px] text-slate-500">
                Дырка, если пропуск дольше интервала связи: {data.connection_period_label || 'день'}
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-950/50">
              <p className="text-[10px] uppercase tracking-wide text-slate-500">Учтено замеров</p>
              <p className="text-lg font-semibold tabular-nums text-slate-900 dark:text-white">
                {data.sample_count}
              </p>
              <p className="text-[11px] text-slate-500">Ни один минимум не отброшен</p>
            </div>
          </div>

          <div className={`mt-3 h-72 cursor-crosshair select-none transition-opacity ${isLoading ? 'opacity-55' : 'opacity-100'}`}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={chartData}
                margin={{ top: 10, right: 4, left: 0, bottom: 0 }}
                onMouseDown={beginSelection}
                onMouseMove={moveSelection}
                onMouseUp={finishSelection}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="bucket_ms"
                  type="number"
                  scale="time"
                  domain={['dataMin', 'dataMax']}
                  minTickGap={32}
                  tick={{ fontSize: 10 }}
                  stroke="#94a3b8"
                  tickFormatter={(value) => formatAxisDate(value, period)}
                />
                <YAxis
                  type="number"
                  domain={[
                    (minimum) => Math.floor((minimum - 50) / 100) * 100,
                    (maximum) => Math.ceil((maximum + 50) / 100) * 100,
                  ]}
                  tick={{ fontSize: 10 }}
                  stroke="#94a3b8"
                  tickFormatter={(value) => (Number(value) / 1000).toFixed(1)}
                  width={36}
                  allowDataOverflow={false}
                />
                <Tooltip content={<VoltageTooltip />} />
                {voltageGradient ? (
                  <>
                    <defs>
                      <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
                        {voltageGradient.stops.map((stop) => (
                          <stop
                            key={stop.key}
                            offset={stop.offset}
                            stopColor={stop.color}
                            stopOpacity={stop.color === 'transparent' ? 0 : 1}
                          />
                        ))}
                      </linearGradient>
                    </defs>
                    <ReferenceArea
                      x1={voltageGradient.x1}
                      x2={voltageGradient.x2}
                      y1={0}
                      y2={5000}
                      fill={`url(#${gradientId})`}
                      fillOpacity={0.16}
                      stroke="none"
                      ifOverflow="hidden"
                    />
                  </>
                ) : null}
                {selection ? (
                  <ReferenceArea
                    x1={Math.min(selection.start, selection.end)}
                    x2={Math.max(selection.start, selection.end)}
                    y1={0}
                    y2={5000}
                    fill="#0284c7"
                    fillOpacity={0.18}
                    stroke="#0284c7"
                    ifOverflow="hidden"
                  />
                ) : null}
                <Line
                  type="linear"
                  dataKey="minimum_voltage_mv"
                  name="Минимум"
                  stroke="#475569"
                  strokeWidth={1.75}
                  connectNulls={false}
                  isAnimationActive={false}
                  dot={<VoltageDot />}
                  activeDot={{ r: 5, fill: '#475569', stroke: '#fff', strokeWidth: 2 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-1 flex items-center gap-2">
            <span className="text-[10px] text-slate-400">ниже</span>
            <span
              className="h-1.5 flex-1 rounded-full"
              style={{ background: 'linear-gradient(90deg, hsl(0 75% 42%), hsl(60 75% 42%), hsl(120 75% 42%))' }}
            />
            <span className="text-[10px] text-slate-400">выше</span>
          </div>
          <p className="mt-1 text-[10px] text-slate-400">
            Цвет от 2.5 В к 3.6 В. Ноль не считается напряжением, просадка внутри интервала не теряется.
            Выделите участок графика — скачаются сырые замеры за него.
          </p>
        </>
      )}
    </section>
  );
}
